import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiChipWithListView,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
  TableHeaderSearchIcon,
} from "../../../../../../../components";
import MuiEllipseLabel from "../../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  getCustomDateFormatWithTime,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../../../helpers/types/common";
import {
  arrayOfObjectsIncludesValue,
  buildActionMenuItems,
  checkFilterMatch as checkFilterMatchUtil,
  compareDates,
  convertToUserFormat,
  convertToUserFormatWithLocalTime,
  getUniqueCountriesFromData,
  getValueOrBlank,
  isDateInRange,
  isNotEmpty,
  stringIncludesArrayOfStringsValue,
} from "../../../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../redux/slices/appState/appStateSelector";
import {
  getCoachNamesWithType,
  getInfoMessageForAssignedCoaches,
} from "../../../../../helper";
import {
  CaseLoadsVsEngagementsResultForm,
  CaseLoadsVsEngagementsResultFormKey,
  CaseLoadsVsEngagementsResultRowValue,
  UniqueParticipantSearchResultFiltersTypes,
} from "../../../../../modelsAndSchemas/Models";
import { myCaseloadsSiteCoreKeys } from "../../../../../sitecore/SiteCoreKeys";
import { MyCaseLoadsEngagementsResultsWrapper } from "./MyCaseLoadsVsEngagementsResults.style";

const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_MY_CASELOADS_RESULTS;

interface MyCaseLoadsEngagementsResultsProps {
  rows: CaseLoadsVsEngagementsResultRowValue[];
  emptyMessageKey: string;
}
export const MyCaseLoadsEngagementsResults: React.FC<
  MyCaseLoadsEngagementsResultsProps
> = ({ rows, emptyMessageKey }) => {
  const { useFetchLabel, fetchValueByKeyOfDropdown, commonLabels } =
    useSiteCore();
  const navigate = useNavigate();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredData, setFilteredData] = useState([...rows]);
  const [uniqueCountriesResults, setUniqueCountriesResults] = useState<
    LabelValue[]
  >([]);
  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueParticipantSearchResultFiltersTypes>(() => ({
      programStatus: [],
    }));
  const [updatedRowsResults, setUpdatedRowsResults] = useState<
    CaseLoadsVsEngagementsResultRowValue[]
  >([]);

  const {
    myCaseloadsTitle,
    participantNameTableColumn: participantNameLabel,
    programTemplateTableColumn: programTemplateLabel,
    participantStatusTableColumn: participantStatusLabel,
    nextContactDateTableColumn: nextContactDateLabel,
    upcomingSessionTableColumn: upcomingSessionLabel,
    programEndDateTableColumn: programEndDateLabel,
    assignedCoachesTableColumn: assignedCoachesLabel,
    participantEmailTableColumn: participantEmailLabel,
    participantMobileNumberTableColumn: participantMobileNumberLabel,
    projectManagerTableColumn: projectManagerLabel,
    countryTableColumn: countryLabel,
    programStartMonthYearTableColumn: programStartMonthYearLabel,
    actionTableColumn: actionLabel,
    actionTableColumnManage: manageLabel,
    participantStatusList,
  } = myCaseloadsSiteCoreKeys;
  const getLabel = useFetchLabel(myCaseloadsTitle);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const { control, setValue, getValues, reset } =
    useForm<CaseLoadsVsEngagementsResultForm>({
      mode: "all",
      defaultValues: {
        [CaseLoadsVsEngagementsResultFormKey.participantName]: "",
        [CaseLoadsVsEngagementsResultFormKey.programTemplateName]: "",
        [CaseLoadsVsEngagementsResultFormKey.programStatus]: "",
        [CaseLoadsVsEngagementsResultFormKey.nextContactDate]: "",
        [CaseLoadsVsEngagementsResultFormKey.upcomingSession]: "",
        [CaseLoadsVsEngagementsResultFormKey.programEndDate]: "",
        [CaseLoadsVsEngagementsResultFormKey.coaches]: "",
        [CaseLoadsVsEngagementsResultFormKey.participantEmail]: "",
        [CaseLoadsVsEngagementsResultFormKey.participantMobileNumber]: "",
        [CaseLoadsVsEngagementsResultFormKey.projectManager]: "",
        [CaseLoadsVsEngagementsResultFormKey.countryIds]: [],
        [CaseLoadsVsEngagementsResultFormKey.programStartDate]: "",
      },
    });
  const formValues = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const rowsDataFields = [...rows];
    const uniqueCountriesDataFields =
      getUniqueCountriesFromData(rowsDataFields);
    setUniqueCountriesResults(uniqueCountriesDataFields);
    reset();
  }, [rows]);

  const getUniqueFiltersForCPParticipants = (
    resultsDataFields: CaseLoadsVsEngagementsResultRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        resultsDataFields
          .filter((fieldItem) => fieldItem[key]) // Filter out null or undefined values
          .map((fieldItem) => fieldItem[key]) // Extract values for the given key
          .reduce((accumulator, value) => accumulator.concat(value), []), // Flatten the array if values are arrays
      ),
    ).map((fieldItem) => ({
      value: fieldItem as string,
      label: fieldItem as string,
    })); // Format as value-label pairs
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localRowsFields = [...rows];

      localRowsFields.forEach((data) => {
        data.coachNamesArray = getCoachNamesWithType(
          data.coaches,
          getValueOrBlank(loggedInUser?.id),
        );

        data.statusKey = data.programStatus;

        data.statusValue = fetchValueByKeyOfDropdown(
          myCaseloadsTitle,
          participantStatusList,
          data.programStatus,
        );
      });
      setUpdatedRowsResults(localRowsFields);
      setFilteredData([...localRowsFields]);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [CaseLoadsVsEngagementsResultFormKey.programStatus]:
          getUniqueFiltersForCPParticipants(localRowsFields, "statusValue"),
      }));
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(updatedRowsResults)) {
      const {
        participantName: participantNameFilterValue,
        programTemplateName: programTemplateNameFilterValue,
        nextContactDate: nextContactDateFilterValue,
        upcomingSession: upcomingSessionFilterValue,
        countryIds: countryIdsFilterValue,
        coaches: coachNameFilterValue,
        programStatus: programStatusFilterValue,
        participantEmail: participantEmailFilterValue,
        participantMobileNumber: participantMobileNumberFilterValue,
        projectManager: projectManagerFilterValue,
        programStartDate: programStartDateFilterValue,
        programEndDate: programEndDateFilterValue,
      } = getValues();

      const myData = updatedRowsResults.filter((item) => {
        const hasProgramTemplateMatch = checkFilterMatchUtil(
          programTemplateNameFilterValue,
          item.programTemplateName,
        );
        const hasNextContactDateMatch = compareDates(
          getValueOrBlank(nextContactDateFilterValue),
          getValueOrBlank(item?.nextContactDate),
          getValueOrBlank(userCountryDateFormat),
        );

        const hasUpcomingSessionDateMatch = compareDates(
          getValueOrBlank(upcomingSessionFilterValue),
          getValueOrBlank(item?.upcomingSession),
          getValueOrBlank(userCountryDateFormat),
        );

        const hasCoachNamesMatch = arrayOfObjectsIncludesValue(
          coachNameFilterValue,
          item.coachNamesArray,
          "label",
        );

        const isParticipantNameMatch = checkFilterMatchUtil(
          getValueOrBlank(participantNameFilterValue),
          getValueOrBlank(item.participantName),
        );

        const isParticipantMobileNumberMatch = checkFilterMatchUtil(
          getValueOrBlank(participantMobileNumberFilterValue),
          getValueOrBlank(item.participantMobileNumber),
        );

        const isParticipantEmailMatch = checkFilterMatchUtil(
          getValueOrBlank(participantEmailFilterValue),
          getValueOrBlank(item.participantEmail),
        );

        const isProjectManagerMatch = checkFilterMatchUtil(
          getValueOrBlank(projectManagerFilterValue),
          getValueOrBlank(item.projectManager),
        );

        const hasCountryMatch = stringIncludesArrayOfStringsValue(
          countryIdsFilterValue,
          item.country,
        );

        const isStatusMatch =
          programStatusFilterValue === "" ||
          item.statusValue?.toLowerCase() ===
            programStatusFilterValue?.toLowerCase();

        let isMatchingProgramStartDate = true;

        if (programStartDateFilterValue) {
          if (!item?.programStartDate) {
            isMatchingProgramStartDate = false;
          } else {
            const userSelectedDate = dayjs(
              programStartDateFilterValue,
              userCountryDateFormat,
            );
            const selectedDateStartOfMonth = userSelectedDate.startOf("month");
            const selectedDateEndOfMonth = userSelectedDate.endOf("month");

            const itemDate = dayjs(item?.programStartDate);

            isMatchingProgramStartDate = isDateInRange(
              itemDate,
              selectedDateStartOfMonth,
              selectedDateEndOfMonth,
            );
          }
        }
        const isMatchingProgramEndDate = compareDates(
          getValueOrBlank(programEndDateFilterValue),
          getValueOrBlank(item?.programEndDate),
          getValueOrBlank(userCountryDateFormat),
        );

        return (
          hasProgramTemplateMatch &&
          hasNextContactDateMatch &&
          hasUpcomingSessionDateMatch &&
          hasCoachNamesMatch &&
          isStatusMatch &&
          isMatchingProgramStartDate &&
          isMatchingProgramEndDate &&
          hasCountryMatch &&
          isParticipantNameMatch &&
          isParticipantMobileNumberMatch &&
          isParticipantEmailMatch &&
          isProjectManagerMatch
        );
      });
      setFilteredData([...myData]);
      resetPagination();
    }
  }, [formValues]);

  const handleManageItemActionClick = (id: string) => {
    navigate(`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${id}`);
  };

  const columns = [
    {
      id: CaseLoadsVsEngagementsResultFormKey.participantName,
      label: getLabel(participantNameLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-participant-name",
      render: (row: any) => {
        if (!row.participantName) return null;
        else {
          return hasModulePermission(MODULE_PERMISSIONS.COACH_PORTAL_VIEW) ? (
            <MuiLink
              href={`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${row.id}`}
              isOpenNewTab
              id={`${pageId}-manage-participant`}
              data-testid="manage-participant-link"
            >
              <MuiBox data-testid={`participant-name-link-${row.id}`}>
                <MuiEllipseLabel
                  data-testid={`participant-name-link-${row.id}`}
                  labelTypoClassName="Link participant-name-link"
                  text={row.participantName}
                  labelTypoVariant="h3"
                  placement="bottom-start"
                  pointerCursor
                />
              </MuiBox>
            </MuiLink>
          ) : (
            <MuiBox>
              <MuiTypography variant="h3" className="">
                {row.participantName}
              </MuiTypography>
            </MuiBox>
          );
        }
      },
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.programTemplateName,
      label: getLabel(programTemplateLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.programStatus,
      label: getLabel(participantStatusLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.statusValue} />
      ),
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.nextContactDate,
      label: getLabel(nextContactDateLabel),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.nextContactDate)
              ? convertToUserFormat(row.nextContactDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.upcomingSession,
      label: getLabel(upcomingSessionLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row?.upcomingSession)
              ? convertToUserFormatWithLocalTime(
                  row.upcomingSession,
                  getCustomDateFormatWithTime(userCountryDateFormat),
                )
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.programEndDate,
      label: getLabel(programEndDateLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.programEndDate)
              ? convertToUserFormat(row.programEndDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.coaches,
      label: getLabel(assignedCoachesLabel),
      isSortVisible: false,
      isEmptyValue: true,
      render: (row: any) =>
        isNotEmpty(row.coachNamesArray) ? (
          <MuiBox className="cell-assigned-coaches">
            <MuiChipWithListView
              data={row.coachNamesArray}
              headerLabel={getLabel(assignedCoachesLabel)}
              gridRef={React.createRef<HTMLElement>()}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
              countToWrap={1}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              infoMessage={getInfoMessageForAssignedCoaches(
                row.coachNamesArray,
              )}
            />
          </MuiBox>
        ) : (
          EMPTY_VALUE_DASH
        ),
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.participantEmail,
      label: getLabel(participantEmailLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.participantMobileNumber,
      label: getLabel(participantMobileNumberLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.projectManager,
      label: getLabel(projectManagerLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.countryName,
      label: getLabel(countryLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-country",
    },
    {
      id: CaseLoadsVsEngagementsResultFormKey.programStartDate,
      label: getLabel(programStartMonthYearLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row.programStartDate)
              ? dayjs(row.programStartDate).format(DATE_FORMAT.MMMM_YYYY)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(actionLabel),
      render: (row: RowProps) => {
        const myCaseLoadsEngagementsResultActions = [
          {
            permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_EDIT,
            create: () => ({
              onClick: () =>
                handleManageItemActionClick(getValueOrBlank(row.id)),
              dataTestId: `${pageId}-manage`,
              label: getLabel(manageLabel),
              ariaLabel: getLabel(manageLabel),
            }),
          },
        ];

        const myCaseLoadsEngagementsResultMenuItems = buildActionMenuItems(
          row,
          myCaseLoadsEngagementsResultActions,
          hasModulePermission,
        );

        return myCaseLoadsEngagementsResultMenuItems ? (
          <MuiOptionMenu menuItems={myCaseLoadsEngagementsResultMenuItems} />
        ) : null;
      },
    },
  ];

  const handleProgramStartDateChange = (
    key: string,
    newValue: dayjs.Dayjs | null,
  ) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(key, dateValue);
  };

  const tableHeaderForm = [
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.participantName}
      key={CaseLoadsVsEngagementsResultFormKey.participantName}
    >
      <MuiTextField
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.participantName}`}
        data-testid="search-field"
        startAdornment={<TableHeaderSearchIcon />}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.programTemplateName}
      key={CaseLoadsVsEngagementsResultFormKey.programTemplateName}
    >
      <MuiTextField
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.programTemplateName}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.programStatus}
      key={CaseLoadsVsEngagementsResultFormKey.programStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.programStatus}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.programStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.nextContactDate}
      key={CaseLoadsVsEngagementsResultFormKey.nextContactDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().nextContactDate !== ""
              ? dayjs(getValues().nextContactDate, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              CaseLoadsVsEngagementsResultFormKey.nextContactDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.upcomingSession}
      key={CaseLoadsVsEngagementsResultFormKey.upcomingSession}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().upcomingSession !== ""
              ? dayjs(getValues().upcomingSession, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              CaseLoadsVsEngagementsResultFormKey.upcomingSession,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.programEndDate}
      key={CaseLoadsVsEngagementsResultFormKey.programEndDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().programEndDate !== ""
              ? dayjs(getValues().programEndDate, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              CaseLoadsVsEngagementsResultFormKey.programEndDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.coaches}
      key={CaseLoadsVsEngagementsResultFormKey.coaches}
    >
      <MuiTextField
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.coaches}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.participantEmail}
      key={CaseLoadsVsEngagementsResultFormKey.participantEmail}
    >
      <MuiTextField
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.participantEmail}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.participantMobileNumber}
      key={CaseLoadsVsEngagementsResultFormKey.participantMobileNumber}
    >
      <MuiTextField
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.participantMobileNumber}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.projectManager}
      key={CaseLoadsVsEngagementsResultFormKey.projectManager}
    >
      <MuiTextField
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.projectManager}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.countryIds}
      key={CaseLoadsVsEngagementsResultFormKey.countryIds}
    >
      <MuiMultiSelect
        label=""
        disabled={false}
        showSearch={true}
        showChip={false}
        textFieldWidth={0}
        options={uniqueCountriesResults}
        id={`${pageId}-${CaseLoadsVsEngagementsResultFormKey.countryIds}`}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      control={control}
      name={CaseLoadsVsEngagementsResultFormKey.programStartDate}
      key={CaseLoadsVsEngagementsResultFormKey.programStartDate}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          fieldMonthPlaceholder: () => commonLabels.monthPlaceHolder,
          fieldYearPlaceholder: () => commonLabels.yearPlaceHolder,
        }}
      >
        <MuiDateField
          value={
            getValues().programStartDate !== ""
              ? dayjs(getValues().programStartDate, userCountryDateFormat)
              : null
          }
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          views={["month", "year"]}
          clearable
          onChange={(newValue) =>
            handleProgramStartDateChange(
              CaseLoadsVsEngagementsResultFormKey.programStartDate,
              newValue,
            )
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,

    <></>,
  ];

  return (
    <MyCaseLoadsEngagementsResultsWrapper>
      <MuiGrid container>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
              columns={columns}
              tableResults={[...filteredData]}
              noDataInApi={rows?.length === 0}
              emptyMessage={getLabel(emptyMessageKey)}
              noResultMessage={commonLabels.noResult}
              isShowDashIfValueEmpty
              priorityResults={[]}
              isLastColumnSticky
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </MyCaseLoadsEngagementsResultsWrapper>
  );
};
